import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../context/LanguageContext';
import { useHelpGuide } from '../context/HelpGuideContext';
import AboutPage from '../pages/AboutPage';
import TermsPage from '../pages/TermsPage';
import PrivacyPage from '../pages/PrivacyPage';
import RefundPage from '../pages/RefundPage';
import FaqPage from '../pages/FaqPage';
import { BUSINESS, SOCIAL, businessAddressBlock } from '../config/business';
import { WHATSAPP_CHANNEL_URL } from '../config/env.js';
import FooterTestimonials from './FooterTestimonials';
import FooterAppStores from './FooterAppStores';
import './Footer.css';

const LINK_GROUPS = [
  {
    titleKey: 'footerCompany',
    links: [
      { action: 'help', key: 'helpGuideTitle' },
      { action: 'about', key: 'about' },
      { href: '/contact', key: 'contact' },
      { action: 'faq', key: 'faqTitle' },
    ],
  },
  {
    titleKey: 'footerLegal',
    links: [
      { href: '/terms', key: 'footerTerms' },
      { href: '/privacy', key: 'footerPrivacy' },
      { href: '/refund', key: 'footerRefund' },
      { href: '/cancellation', key: 'footerCancellation' },
    ],
  },
];

export default function Footer() {
  const { t } = useLang();
  const { openHelpGuide, registerFaqOpener } = useHelpGuide();
  const [modals, setModals] = useState({
    about: false,
    faq: false,
    terms: false,
    privacy: false,
    refund: false,
  });

  const open = useCallback((key) => setModals((m) => ({ ...m, [key]: true })), []);
  const close = (key) => setModals((m) => ({ ...m, [key]: false }));

  useEffect(() => registerFaqOpener(() => open('faq')), [registerFaqOpener, open]);

  const handleAction = (action) => {
    if (action === 'help') openHelpGuide();
    else open(action);
  };

  return (
    <>
      {modals.about && <AboutPage onClose={() => close('about')} />}
      {modals.faq && <FaqPage onClose={() => close('faq')} />}
      {modals.terms && <TermsPage onClose={() => close('terms')} />}
      {modals.privacy && <PrivacyPage onClose={() => close('privacy')} />}
      {modals.refund && <RefundPage onClose={() => close('refund')} />}

      <footer className="app-footer" data-sub-free>
        <div className="app-footer__wrap">
          <section className="app-footer__hero">
            <div className="app-footer__hero-pattern" aria-hidden />
            <div className="app-footer__hero-body">
              <img src="/rk-logo.webp" alt={t.brandName} className="app-footer__logo" />
              <p className="app-footer__sub">{t.paperSub}</p>
            </div>
            <FooterTestimonials />
            <p className="app-footer__trust">{t.trustStrip}</p>
            <div className="app-footer__socials">
              {WHATSAPP_CHANNEL_URL && (
                <a
                  href={WHATSAPP_CHANNEL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="app-footer__social app-footer__social--wa"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 0 0 .914.914l4.458-1.495A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                  </svg>
                  {t.whatsappChannel}
                </a>
              )}
              <a
                href={SOCIAL.youtube}
                target="_blank"
                rel="noreferrer"
                className="app-footer__social app-footer__social--yt"
              >
                {t.youtube}
              </a>
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noreferrer"
                className="app-footer__social app-footer__social--ig"
              >
                {t.instagram}
              </a>
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noreferrer"
                className="app-footer__social app-footer__social--fb"
              >
                {t.facebook}
              </a>
            </div>
          </section>

          <section className="app-footer__panel">
            <div className="app-footer__panel-top">
              {LINK_GROUPS.map((group) => (
                <div key={group.titleKey} className="app-footer__col">
                  <h3 className="app-footer__col-title">
                    <span className="app-footer__col-bar" aria-hidden />
                    {t[group.titleKey]}
                  </h3>
                  <ul className="app-footer__col-links">
                    {group.links.map((link) => (
                      <li key={link.key}>
                        {link.href ? (
                          <a href={link.href}>{t[link.key]}</a>
                        ) : (
                          <button type="button" onClick={() => handleAction(link.action)}>
                            {t[link.key]}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="app-footer__col app-footer__col--contact">
                <h3 className="app-footer__col-title">
                  <span className="app-footer__col-bar" aria-hidden />
                  {t.footerContact}
                </h3>
                <a href="mailto:contact@repatikosam.com" className="app-footer__email">
                  contact@repatikosam.com
                </a>
                <p className="app-footer__legal-entity">{BUSINESS.legalName}</p>
                <p className="app-footer__gst">
                  {t.gstLabel}: {BUSINESS.gstin}
                </p>
                <p className="app-footer__address">{businessAddressBlock()}</p>
              </div>

              <div className="app-footer__col app-footer__col--stores">
                <FooterAppStores />
              </div>
            </div>
          </section>

          <p className="app-footer__copy">
            © {new Date().getFullYear()} {BUSINESS.legalName} · {t.brandName}
            <span className="app-footer__sep"> · </span>
            {t.footerMadeInAp}
          </p>
        </div>
      </footer>
    </>
  );
}
